import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  untracked,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms'

import { TooltipModule } from 'primeng/tooltip'

import { FlxFormControlModule } from '@flowx/angular-ui-toolkit'
import { FlxSpinnerModule } from '@flowx/designer/shared/components/spinner'
import {
  ConflictingResource,
  CopyResourceFromType,
  ExtendedConflictingResource,
} from '@flowx/designer/shared/model/copy-resource.model'
import { CopyResourceService } from '@flowx/designer/shared/services/copy-resource.service'

import { resourceTypeReadable } from '../../model/resource-usage.model'

@Component({
  selector: 'flx-copy-resource-review-resources',
  templateUrl: 'copy-resource-review-resources.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FlxFormControlModule, ReactiveFormsModule, TooltipModule, FlxSpinnerModule],
  host: { class: 'overflow-auto' },
})
export class CopyResourceReviewResourcesComponent {
  #copyResourceService = inject(CopyResourceService)

  conflictingResourcesFA = this.#copyResourceService.conflictingResourcesFA
  applyToAllFC = this.#copyResourceService.applyToAllFC
  sourceAppName = this.#copyResourceService.sourceAppName
  targetAppName = this.#copyResourceService.applicationFC.value.name

  applyToAllOptionLabel = 'Apply to all'

  applyToAllOptions = [
    {
      label: this.applyToAllOptionLabel,
      value: CopyResourceFromType.BOTH,
    },
    {
      label: this.applyToAllOptionLabel,
      value: CopyResourceFromType.SOURCE,
    },
    {
      label: this.applyToAllOptionLabel,
      value: CopyResourceFromType.TARGET,
    },
  ]

  resourcesComparison = this.#copyResourceService.resourcesComparison
  conflictingResources = this.#copyResourceService.conflictingResources
  conflictingResourceValues = computed(() => Object.values(this.conflictingResources()))

  subtitle = computed(() => {
    const conflictingResources = this.conflictingResourceValues()

    if (conflictingResources.length) {
      const isSingleConflict = conflictingResources.length === 1
      const resourceString = isSingleConflict ? 'resource' : 'resources'
      const existsString = isSingleConflict ? 'exists' : 'exist'

      return `${conflictingResources.length} ${resourceString} referenced by ${this.sourceAppName} already ${existsString} with the same identifier name/key/code in ${this.targetAppName}.`
    }

    return ''
  })

  constructor() {
    effect(() => {
      const resourcesComparison = this.resourcesComparison()
      const conflictingResources = untracked(() => this.conflictingResources())
      const conflictingResourceValues = Object.values(conflictingResources)

      if (!conflictingResourceValues.length && resourcesComparison?.conflictingResources.length) {
        const { conflictingResources } = resourcesComparison
        this.setConflictingResourcesAndFormControls(conflictingResources)
      }
    })

    this.conflictingResourcesFA.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      // This logic needs to run even when there is a single form control, because
      // the noNewResourcesToImport in CopyResource depends on it to work properly.
      const previousApplyToAllValue = this.applyToAllFC.value
      const currentApplyToAllValue = this.formControlsSameOption()

      if (previousApplyToAllValue !== currentApplyToAllValue) {
        this.applyToAllFC.setValue(currentApplyToAllValue)
      }
    })
  }

  setConflictingResourcesAndFormControls(conflictingResources: ConflictingResource[]): void {
    const conflictingResourceIds = conflictingResources.map(
      (conflictingResource) => conflictingResource.resourceDefinitionId
    )

    const formattedResources: Record<string, ExtendedConflictingResource> =
      conflictingResources.reduce((resources, conflictingResource, index) => {
        resources[conflictingResource.resourceDefinitionId] = {
          index,
          resourceDefinitionId: conflictingResource.resourceDefinitionId,
          name: conflictingResource.name,
          extendedName: '',
          resourceType: conflictingResource.resourceType,
          parents: conflictingResource.parents,
          conflictingChildrenIds: (conflictingResource.children || []).filter((childId) =>
            conflictingResourceIds.includes(childId)
          ),
          options: [
            {
              label: 'Keep both',
              value: CopyResourceFromType.BOTH,
            },
            {
              label: 'Replace',
              value: CopyResourceFromType.SOURCE,
            },
            {
              label: 'Use destination',
              value: CopyResourceFromType.TARGET,
            },
          ],
        } satisfies ExtendedConflictingResource

        return resources
      }, {})

    Object.values(formattedResources).forEach((resource: ExtendedConflictingResource) => {
      // Prevent adding more form controls when returning from another step.
      if (this.conflictingResourcesFA.length !== conflictingResources.length) {
        const formControl = new FormControl(CopyResourceFromType.BOTH)
        this.conflictingResourcesFA.push(formControl)

        formControl.setValidators([
          Validators.required,
          this.determineChildrenStatusBasedOnParentValues(resource.resourceDefinitionId),
        ])
      }

      const resourceTypeWithName = ` ${resourceTypeReadable[resource.resourceType] || resource.resourceType} / ${resource.name}`
      let parentNames = ''

      if (resource.parents?.length) {
        resource.parents.forEach((parentId) => {
          const parentResourceOption = formattedResources[parentId]

          if (parentResourceOption) {
            parentNames = parentNames
              ? `${parentNames}, ${parentResourceOption.name}`
              : parentResourceOption.name
          }
        })
      }

      resource.extendedName = parentNames
        ? `${resourceTypeWithName} (${parentNames})`
        : resourceTypeWithName
    })

    this.#copyResourceService.conflictingResources.set(formattedResources)
  }

  determineChildrenStatusBasedOnParentValues(resourceDefinitionId: string): ValidatorFn {
    return (control: FormControl) => {
      // If parents have TARGET value or are disabled, disable all children. Works recursively by
      // starting from the selected parent. Also enables children if previous conditions are not met.
      const resourceOption = this.conflictingResources()[resourceDefinitionId]

      if (resourceOption.conflictingChildrenIds.length) {
        const circularReferenceFreeChildren = resourceOption.conflictingChildrenIds.filter(
          (childId) => !resourceOption.parents?.includes(childId)
        )

        circularReferenceFreeChildren.forEach((childId: string) => {
          const childResourceOptionWithFC = this.getResourceOptionWithFormControl(childId)

          this.disableChildrenIfParentsHaveTargetValueOrAreDisabled(
            resourceOption,
            childResourceOptionWithFC.resourceOption,
            control,
            childResourceOptionWithFC.formControl,
            new Set([childId])
          )
        })
      }

      return null
    }
  }

  disableChildrenIfParentsHaveTargetValueOrAreDisabled(
    parentResourceOption: ConflictingResource,
    childResourceOption: ConflictingResource,
    parentFC: FormControl,
    childFC: FormControl,
    checkedIds: Set<string>
  ): void {
    const parentsDisabled = [
      parentFC.value === CopyResourceFromType.TARGET || parentFC.disabled,
      ...this.checkOtherParents(parentResourceOption.resourceDefinitionId, childResourceOption),
    ]

    const isDisabled = parentsDisabled.every((disabled) => disabled)

    if (isDisabled && childFC.enabled) {
      childFC.disable({ emitEvent: false })
    } else if (!isDisabled && childFC.disabled) {
      childFC.enable({ emitEvent: false })
    }

    if (childResourceOption.conflictingChildrenIds.length) {
      childResourceOption.conflictingChildrenIds.forEach((grandchildId: string) => {
        if (!checkedIds.has(grandchildId)) {
          const grandchildResourceOptionWithFC = this.getResourceOptionWithFormControl(grandchildId)

          this.disableChildrenIfParentsHaveTargetValueOrAreDisabled(
            childResourceOption,
            grandchildResourceOptionWithFC.resourceOption,
            childFC,
            grandchildResourceOptionWithFC.formControl,
            checkedIds.add(grandchildId)
          )
        }
      })
    }
  }

  checkOtherParents(
    checkedParentResourceDefinitionId: string,
    childResourceOption: ConflictingResource
  ): boolean[] {
    const parentsDisabled = []

    const parents = childResourceOption.parents.filter(
      (parentId) => parentId !== checkedParentResourceDefinitionId
    )

    if (parents.length) {
      parents.forEach((parentId) => {
        const parentFC = this.getResourceOptionWithFormControl(parentId).formControl
        parentsDisabled.push(parentFC.value === CopyResourceFromType.TARGET || parentFC.disabled)
      })
    }

    return parentsDisabled
  }

  getResourceOptionWithFormControl(resourceDefinitionId: string): {
    resourceOption: ConflictingResource
    formControl: FormControl
  } {
    const resourceOption = this.conflictingResources()[resourceDefinitionId]
    const formControl = this.conflictingResourcesFA.at(resourceOption.index) as FormControl

    return {
      resourceOption,
      formControl,
    }
  }

  formControlsSameOption(): CopyResourceFromType {
    const firstValue = this.conflictingResourcesFA.at(0).value

    const hasSameValue = this.conflictingResourcesFA.value.every(
      (value: CopyResourceFromType) => value === firstValue
    )

    if (hasSameValue) {
      return firstValue
    }

    return null
  }

  applySameOptionToAll(value: CopyResourceFromType): void {
    const formControlValues = Array(this.conflictingResourcesFA.length)
    formControlValues.fill(value)
    this.conflictingResourcesFA.patchValue(formControlValues)
  }
}
